
import { 
  Smartphone, Cpu, Mail, Calendar, Users, CheckSquare
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function Integrations() {
  const integrations = [
    { 
      id: 'google-gmail',
      name: 'Gmail', 
      desc: 'Seamlessly sync and automate your real estate communications.',
      icon: Mail,
      type: 'Communication'
    },
    { 
      id: 'google-calendar',
      name: 'Google Calendar', 
      desc: 'Automated scheduling and property viewing coordination.',
      icon: Calendar,
      type: 'Schedule'
    },
    { 
      id: 'google-contacts',
      name: 'Google Contacts', 
      desc: 'Keep your mobile and desktop address books perfectly in sync.',
      icon: Users,
      type: 'CRM'
    },
    { 
      id: 'google-tasks',
      name: 'Google Tasks', 
      desc: 'Never miss a follow-up with automated task synchronization.',
      icon: CheckSquare,
      type: 'Productivity'
    },
    { 
      id: 'twilio-sms',
      name: 'Twilio SMS', 
      desc: 'High-performance SMS gateway for lead engagement.',
      icon: Smartphone,
      type: 'Communication'
    },
    { 
      id: 'ai-providers',
      name: 'AI Hub', 
      desc: 'Connect Gemini, Claude, OpenAI, DeepSeek, Groq, and more.',
      icon: Cpu,
      type: 'Intelligence'
    }
  ];

  return (
    <div className="pb-32 bg-[var(--t-bg)] text-[var(--t-text)]">
      {/* Hero */}
      <section className="pt-40 pb-20 text-center px-6 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-indigo-600/10 blur-[120px] rounded-full pointer-events-none" />
        <h1 className="text-6xl md:text-8xl font-black mb-8 italic tracking-tighter uppercase pr-4">
          Elite <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Connectivity.</span>
        </h1>
        <p className="text-xl text-[var(--t-text-muted)] max-w-3xl mx-auto mb-12 font-medium">
          WholeScale OS is the central nervous system for your real estate operation, deeply integrated with the mission-critical tools you already use.
        </p>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 mb-40">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {integrations.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="p-10 rounded-[2.5rem] bg-[var(--t-surface)] border border-[var(--t-border)] group hover:border-indigo-500/30 transition-all hover:translate-y-[-8px] shadow-2xl relative overflow-hidden"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 group-hover:scale-110 transition-transform shadow-inner">
                  <item.icon size={32} />
                </div>
                <span className="px-4 py-1.5 rounded-full bg-white/5 text-[10px] font-black uppercase tracking-widest text-[var(--t-text-muted)]">
                  {item.type}
                </span>
              </div>
              
              <h3 className="text-2xl font-black uppercase tracking-tight italic mb-4">{item.name}</h3>
              <p className="text-[var(--t-text-muted)] text-sm leading-relaxed mb-8 font-medium">{item.desc}</p>
              


              {/* Subtle background icon */}
              <div className="absolute -bottom-4 -right-4 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity">
                <item.icon size={120} />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6">
        <div className="p-16 rounded-[4rem] bg-gradient-to-br from-indigo-600 to-purple-800 text-center relative overflow-hidden shadow-2xl border border-white/10 group">
          <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          <h2 className="text-4xl font-black italic uppercase tracking-tighter mb-8 text-white">Missing a Protocol?</h2>
          <p className="text-white/60 mb-12 text-xl max-w-2xl mx-auto font-medium">
            Our API is fully open and sovereign. We can integrate with any infrastructure in your stack.
          </p>
          <Link 
            to="/contact" 
            className="inline-block px-12 py-6 rounded-2xl bg-white text-indigo-600 font-black uppercase tracking-widest hover:scale-105 transition-all shadow-2xl relative z-10"
          >
            Request Custom Bridge
          </Link>
        </div>
      </section>
    </div>
  );
}
